import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { InvoiceRulesOperatorType } from './useCreateRecurringRule';
import useTakerMarkets, { TakerMarket } from './useTakerMarkets';

export interface RecurringRuleData {
  id: string;
  marketId: number;
  takerId: number;
  gteDpe?: number;
  lteDpe?: number;
  fromDueDate?: string;
  toDueDate?: string;
  gteInvoiceAmount?: number;
  lteInvoiceAmount?: number;
  excludedVoucherIds?: string[];
}

export type RecurringRuleCategory = 'dpe' | 'invoiceId' | 'amount' | 'dueDate';

export type RecurringRuleCriteria = Exclude<keyof RecurringRuleData, 'id' | 'marketId' | 'takerId'>;

interface FormattedRecurringRule {
  amount?: {
    gteInvoiceAmount?: RecurringRuleData['gteInvoiceAmount'];
    lteInvoiceAmount?: RecurringRuleData['lteInvoiceAmount'];
  };
  dpe?: {
    gteDpe?: RecurringRuleData['gteDpe'];
    lteDpe?: RecurringRuleData['lteDpe'];
  };
  dueDate?: {
    fromDueDate?: RecurringRuleData['fromDueDate'];
    toDueDate?: RecurringRuleData['toDueDate'];
  };
  excludedVoucherIds?: RecurringRuleData['excludedVoucherIds'];
}

export interface RecurringRule extends FormattedRecurringRule {
  id: RecurringRuleData['id'];
  count: number;
  currency: TakerMarket['currency'];
  makerOrganizationUuid: TakerMarket['makerOrganizationUuid'];
  marketId: RecurringRuleData['marketId'];
  marketUuid: TakerMarket['marketUuid'];
  operatorType: InvoiceRulesOperatorType;
  takerId: RecurringRuleData['takerId'];
  takerUuid: TakerMarket['takerDivisionUuid'];
}

export interface TakerMarketWithRecurringRule {
  rule: RecurringRule;
  takerMarket: TakerMarket;
}

export type SEAInvoiceRulesResponse = paths['/invoice-rules']['get']['responses']['200']['content']['application/json'];

type SEAInvoiceRulesQueryOptions = paths['/invoice-rules']['get']['parameters']['query'];

export type SEAInvoiceRules = SEAInvoiceRulesResponse['data'];

type SEAInvoiceRuleCriteria = SEAInvoiceRules[0]['rules'][0]['criteria'];

export const QUERY_KEY_RECURRING_RULES = 'recurring-rules';

/**
 * formats recurring rule data for frontend use
 * input = { dpe: {gte: 8, lte: 24}, invoiceAmount: {lte: 1000} }
 * output = {
 *   dpe: { gteDpe: 8, lteDpe: 24 },
 *   amount: { lteInvoiceAmount: 1000 }
 * }
 */
export const formatSEAInvoiceRuleAsRecurringRule = (
  rule?: SEAInvoiceRuleCriteria
): FormattedRecurringRule | undefined => {
  if (!rule) {
    return;
  }

  const formattedRecurringRule: FormattedRecurringRule = {};

  if (rule?.dpe?.gte || rule?.dpe?.lte) {
    formattedRecurringRule.dpe = {
      ...(rule?.dpe?.gte ? { gteDpe: rule?.dpe?.gte } : {}),
      ...(rule?.dpe?.lte ? { lteDpe: rule?.dpe?.lte } : {}),
    };
  }

  if (rule?.dueDate?.from || rule?.dueDate?.to) {
    formattedRecurringRule.dueDate = {
      ...(rule?.dueDate?.from ? { fromDueDate: rule?.dueDate?.from } : {}),
      ...(rule?.dueDate?.to ? { toDueDate: rule?.dueDate?.to } : {}),
    };
  }

  if (rule?.invoiceAmount?.gte || rule?.invoiceAmount?.lte) {
    formattedRecurringRule.amount = {
      ...(rule?.invoiceAmount?.gte ? { gteInvoiceAmount: rule?.invoiceAmount?.gte } : {}),
      ...(rule?.invoiceAmount?.lte ? { lteInvoiceAmount: rule?.invoiceAmount?.lte } : {}),
    };
  }

  if (rule?.voucherIds?.exclude) {
    formattedRecurringRule.excludedVoucherIds = rule?.voucherIds?.exclude;
  }

  return formattedRecurringRule;
};

const formatSEAInvoiceRules = (data: SEAInvoiceRules, takerMarkets: TakerMarket[]): TakerMarketWithRecurringRule[] => {
  return data.reduce<TakerMarketWithRecurringRule[]>((formattedRecurringRules, ruleGroup) => {
    const rule = ruleGroup.rules[0]; // It is expected that each rule group will only have one rule when queried with groupingKey SUPPLIER_MARKET

    const takerMarket = takerMarkets.find(
      (takerMarket) =>
        takerMarket.makerOrganizationUuid === rule.supplierMarket.makerOrganizationUuid &&
        takerMarket.takerDivisionUuid === rule.supplierMarket.supplierDivisionUuid &&
        takerMarket.marketUuid === rule.supplierMarket.marketUuid
    );

    if (takerMarket) {
      const formattedRecurringRule = formatSEAInvoiceRuleAsRecurringRule(rule.criteria);

      formattedRecurringRules.push({
        rule: {
          id: ruleGroup.uuid, // runtime uuid of the rule group
          count: Object.keys(formattedRecurringRule ?? {}).length,
          currency: takerMarket.currency,
          makerOrganizationUuid: rule.supplierMarket.makerOrganizationUuid,
          marketId: takerMarket.legacyMarketId,
          marketUuid: rule.supplierMarket.marketUuid,
          operatorType: rule.operatorType,
          takerId: takerMarket.takerDivisionId,
          takerUuid: rule.supplierMarket.supplierDivisionUuid,
          ...formattedRecurringRule,
        },
        takerMarket,
      });
    }

    return formattedRecurringRules;
  }, []);
};

export const fetchSEAInvoiceRules = async (
  takerMarkets: TakerMarket[],
  options?: SEAInvoiceRulesQueryOptions
): Promise<TakerMarketWithRecurringRule[]> => {
  const supplierMarketsFilter = (options && 'supplierMarkets' in options && options.supplierMarkets) || undefined;

  const seaRules = await apiClient
    .get(`api/supplier-experience/invoice-rules`, {
      searchParams: qs.stringify(
        {
          groupingKey: 'SUPPLIER_MARKET',
          supplierMarkets: supplierMarketsFilter,
        } satisfies SEAInvoiceRulesQueryOptions,
        { arrayFormat: 'indices' }
      ),
    })
    .json<SEAInvoiceRulesResponse>();

  return seaRules.data ? formatSEAInvoiceRules(seaRules.data, takerMarkets) : [];
};

const useRecurringRules = (options?: SEAInvoiceRulesQueryOptions): TakerMarketWithRecurringRule[] => {
  const { data: takerMarkets = [] } = useTakerMarkets();

  const enabled = takerMarkets.length > 0;

  const seaRulesQuery = useSuspenseQuery({
    queryKey: options?.supplierMarkets
      ? [QUERY_KEY_RECURRING_RULES, ...options.supplierMarkets]
      : [QUERY_KEY_RECURRING_RULES],
    queryFn: () => (enabled ? fetchSEAInvoiceRules(takerMarkets, options) : null),
  });

  // we are not making API call if there are no taker markets. so returning empty array
  if (takerMarkets.length === 0) {
    return [];
  }

  return seaRulesQuery.isSuccess && seaRulesQuery.data ? seaRulesQuery.data : [];
};

export default useRecurringRules;
